import { IEvaluationScoreOption } from './Company'

export enum PdfExportType {
  JOB_PROFILE = 'jobProfile',
  EVALUATION_FORM = 'evaluationForm',
  FEEDBACK_FORM = 'feedbackForm',
  COLLECTION = 'collection',
  TERM_RESULTS = 'termResults',
}

/**
 * Bulk scope of an export. `level` = a single job profile (Phase 1, params holds
 * `jobLevelId`). The others are bulk (admin-only): all levels of a growth path
 * (`job`, params holds `scopeId` = jobId), a department, or the whole company.
 */
export enum PdfExportScope {
  LEVEL = 'level',
  JOB = 'job',
  DEPARTMENT = 'department',
  COMPANY = 'company',
}

/**
 * Parameters of an export. For job-profile / evaluation-form exports `scope`
 * identifies what was exported (LEVEL carries `jobLevelId`, JOB/DEPARTMENT carry
 * `scopeId`, COMPANY needs neither). Collection exports ignore `scope` and instead
 * carry `collectionId` (+ `memberId`); `asMember` renders the disclosed
 * (employee-visible) view when a lead/admin exports someone else's collection.
 */
export interface IPdfExportParams {
  scope?: PdfExportScope
  jobLevelId?: string
  scopeId?: string
  collectionId?: string
  memberId?: string
  /** Render the disclosed (employee-visible) view of someone else's collection. */
  asMember?: boolean
  /** Term-results exports: which term to export. */
  termNumber?: number
  /** Term-results exports: the member cohort to report on (the tab's active filter). */
  memberIds?: string[]
  /** Term-results exports: labels of the filters that produced `memberIds`, stamped on the cover. */
  filterLabels?: string[]
  /** Stamp the "contains info not shared with the employee" cover banner (full export by a lead/admin). */
  disclosureWarning?: boolean
}

/** Persisted export selection ("recipe") — NOT the rendered PDF. */
export interface IPdfExportPure {
  _id?: string
  companyId: string
  requestedBy: string
  /** Document kind. Only JOB_PROFILE exists today; kept as the discriminator for future types. */
  exportType: PdfExportType
  params: IPdfExportParams
  title: string
  createdAt?: string
}

/** Data a job-profile PDF needs, assembled client-side from the company. */
export interface IJobProfilePdfData {
  jobTitle: string
  jobLevelTitle: string
  jobLevelDescription: string
  department: string
  /** Resolved department brand color (hex) — header background; '' when none */
  departmentColor: string
  /** Department image as a data URI — header background; '' when none / blocked by CORS */
  departmentImage: string
  /** Contrast-checked header text color (hex) for a department-themed header; '' when none */
  departmentHeaderText: string
  /** Department emoji rendered to a PNG data URI (canvas) — header badge; '' when none */
  departmentIcon: string
  /** Short ("header chip") job properties with text on this level — rendered as
   * pills in the profile header (mirrors the cultural-phase pill) */
  headerChips: Array<{ label: string; text: string }>
  /** Long ("bottom text") job properties with text on this level, rendered in the
   * bottom "Job details" section (when the global PDF "show job properties" toggle
   * is on); public exports use the public set */
  properties: Array<{ label: string; text: string }>
  /** Whether any bottom-text job properties are exportable — gates the "Evaluation criteria" umbrella */
  hasExportProperties: boolean
  skills: Array<{
    theme: string
    title: string
    summary: string
    description: string
  }>
  culturalPhase: string
  /** Titles of the job levels this one reports to; empty when none is set */
  reportsTo: string[]
  cultureValues: Array<{
    value: string
    mantra: string
    description: string
    /** Resolved hex color string (value label + icon) */
    color: string
    /** Darker hex variant (mantra) */
    mantraColor: string
    /** Light hex tint (icon-box background + full-colour-card background) */
    bgColor: string
    behaviors: string
  }>
  companyName: string
  /** Logo as a data URI (or '' when unavailable / blocked by CORS) */
  companyLogo: string
  /** Company-wide visibility toggles (absent setting = show) */
  display: {
    hideSkillSummaries: boolean
    hideValueDescriptions: boolean
    hideIcons: boolean
    /** One tall page sized to content instead of A4 pagination */
    continuousPage: boolean
    /** Show the job-property header chips + bottom-text block. Set per document
     * (job-profile `display` vs evaluation-form `evaluationDisplay`). */
    showJobProperties: boolean
    /** Give each culture card a light-tint background + full-colour border (job-profile PDF only) */
    fullColorCultureCards: boolean
  }
  /** Hex color tokens derived from companyColors (maps to the Figma UI ramp) */
  colors: {
    primary: string // ui/primary
    primaryText: string // contrast-checked text to place ON the primary color
    text: string // ui/15 — titles, values
    textMid: string // ui/30 — section labels, item titles
    accent: string // ui/48 — section-header subtitles
    border: string // ui/85 — dividers
    bgTint: string // ui/95 — header bg, icon-box bg
    paper: string // page/card background (white)
  }
}
/**
 * Data a printable evaluation-form PDF needs. It is the job-profile data (same
 * header, and the skills + culture values resolved for the job level) plus the
 * two company score scales, rendered as blank tick rows for a hand-filled form.
 * The form page is always A4-paginated and ignores the profile-only fields it
 * doesn't use (properties, continuousPage).
 */
export interface IEvaluationFormPdfData extends IJobProfilePdfData {
  skillScale: IEvaluationScoreOption[]
  cultureScale: IEvaluationScoreOption[]
}

/**
 * Data a printable feedback-form PDF needs: the job-profile data as is (header,
 * skills and culture values for the job level). A colleague writes a top and a
 * tip per competency by hand, so there is nothing to add.
 */
export type IFeedbackFormPdfData = IJobProfilePdfData

/** Grouping level a bulk cover/separator page represents. */
export type PdfCoverLevel = 'company' | 'department' | 'job'

/**
 * One entry in a bulk export's document order: either a cover/separator page for
 * a grouping (company / department / job) or a single job profile. Covers exist
 * only at grouping boundaries — never between sibling profiles.
 */
export type PdfBulkEntry =
  | {
      type: 'cover'
      level: PdfCoverLevel
      /** Path from the company root: [company], [company, dept], [company, dept, job]. */
      breadcrumb: string[]
      /**
       * Names of the immediate child clusters listed on the cover: company →
       * departments (or jobs when there are none), department → jobs, job → its
       * levels.
       */
      contents: string[]
      /**
       * Owning department (domain) id. Set on department covers AND on the
       * growth-path (job) covers nested under one, so a job cover can inherit its
       * domain's brand color.
       */
      departmentId?: string
    }
  | { type: 'profile'; jobLevelId: string }

/** Data a bulk cover/separator page needs (assembled client-side). */
export interface IPdfCoverData {
  /** Eyebrow label, e.g. "Baanprofielen" */
  title: string
  /** Path from the company root (rendered deepest-first: deepest big, parents small). */
  breadcrumb: string[]
  /** Names of the immediate child clusters, listed under the title. */
  contents: string[]
  /** Logo as a data URI (or '' when unavailable) */
  companyLogo: string
  /** Pre-formatted export date line, e.g. "Geëxporteerd op 15-06-2026" */
  exportDate: string
  level: PdfCoverLevel
  departmentId?: string
  /** Resolved department (domain) brand color (hex) — cover background; absent when none. Also set on growth-path covers that belong to a colored domain. */
  departmentColor?: string
  /** Department image as a data URI — department-cover background only; absent on growth-path covers / when none / blocked by CORS */
  departmentImage?: string
  /** Contrast-checked cover text color (hex) for a department-themed cover; absent when none */
  departmentTextColor?: string
  colors: IJobProfilePdfData['colors']
}

/** Ordered page of a combined bulk document: a cover or a job profile. */
export type PdfBulkPage =
  | { type: 'cover'; cover: IPdfCoverData }
  | { type: 'profile'; data: IJobProfilePdfData }

// ---------------------------------------------------------------------------
// Collection (term-results) PDF — one member's evaluation results for one term.
// Assembled client-side from the already visibility-filtered collection, so it
// carries only what the exporter is allowed to see (see buildCollectionExportData).
// ---------------------------------------------------------------------------

export type CollectionSectionKey = 'skills' | 'culture' | 'goals'

/** Why a section shows no results (mirrors the on-screen StatCard reveal state). */
export type CollectionRevealState = 'revealed' | 'pending' | 'empty' | 'noScore'

// The data carries no localised copy — section titles, reveal notes, goal
// status labels and the disclosure banner are supplied by the labels/component
// (registry pattern). Company content (scale-step labels, competency names) is
// not i18n and stays on the data.

/** One assessor's individual comment on a competency (the on-screen drill-down row). */
export interface ICollectionPdfComment {
  assessorName: string
  /** Base64 data URI of the assessor's photo, or '' → PdfAvatar renders the initials fallback. */
  avatar: string
  /** Pre-formatted submit date, e.g. "12-06-2026" (or '' when unknown). */
  date: string
  /** That assessor's own closest scale-step label (company scale copy, not i18n; absent for goals). */
  scoreLabel?: string
  text: string
}

/** One competency's score box: a skill / culture value (segmented bar) or a goal. */
export interface ICollectionPdfCompetency {
  title: string
  /** Resolved hex colour (value colour for culture, section colour otherwise). */
  color: string
  /** 0–100 aggregate for this competency (absent when it produced no score). */
  score?: number
  /** Previous term's aggregate for the "vs last term" delta pill (absent when no baseline). */
  previous?: number
  /** Closest scale-step label for the score (company scale copy, not i18n). */
  stepLabel?: string
  /** Segmented-bar fill (skills / culture). */
  segments?: { total: number; complete: number }
  /** Goals only: whether the goal counts as achieved. */
  goalAchieved?: boolean
  /** Individual assessor comments for this competency (v2 full-collection export). */
  comments?: ICollectionPdfComment[]
  /** Visible assessors bucketed per scale step (aligned to `segments.total`) — the
   * score-distribution avatars under the bar. Gated by scoreVisibility; skills/culture only. */
  assessorSteps?: Array<Array<{ name: string; avatar: string }>>
}

/** A results section (Skills / Culture / Goals): overview + per-competency boxes. */
export interface ICollectionPdfSection {
  key: CollectionSectionKey
  reveal: CollectionRevealState
  /** Section aggregate — present only when revealed with a score. */
  overview?: {
    /** 0–100 (skills / culture average, or goals achievement %). */
    value?: number
    /** Previous term's aggregate for the "vs last term" delta pill (absent when no baseline). */
    previous?: number
    /** Closest scale-step label (company scale copy). */
    stepLabel?: string
    segments?: { total: number; complete: number }
    /** Goals render as a percentage instead of a segmented scale. */
    isGoals?: boolean
    goalsAchieved?: number
    goalsTotal?: number
    assessorsSubmitted: number
    assessorsTotal: number
  }
  competencies: ICollectionPdfCompetency[]
}

/** Data a collection PDF needs, assembled client-side from the loaded collection. */
export interface ICollectionPdfData {
  memberName: string
  jobTitle: string
  /** True when the job differs from the one held at the previous collection. */
  jobChanged?: boolean
  /** Term display name, e.g. "New era" or "#3". */
  termLabel: string
  /** Pre-formatted term period, e.g. "12-06-2026 – 12-09-2026" (or '' when unknown). */
  termPeriod: string
  leads: string[]
  buddies: string[]
  companyName: string
  /** Logo as a data URI (or '' when unavailable / blocked by CORS). */
  companyLogo: string
  colors: IJobProfilePdfData['colors']
  /** One tall page per section instead of A4 pagination. */
  display: { continuousPage: boolean }
  /** True when a lead/admin exported the "full" view — stamps a cover warning. */
  disclosureWarning: boolean
  /** A previous term existed to compare against — gates the section delta pills. */
  compareActive: boolean
  /** Score-trend line-graph (all terms), the selected term highlighted. Absent when no history. */
  trend?: {
    points: Array<{
      termNumber: number
      /** Term display name for the x-axis label. */
      label: string
      /** 0–100 skill / culture average that term, or null → placeholder dot at 50. */
      skill: number | null
      culture: number | null
    }>
    selectedTermNumber: number
  }
  /** Received feedback, per giver: their tips/tops grouped by competency. Absent when none. */
  feedback?: {
    items: Array<{
      giverName: string
      /** Base64 data URI, or '' → PdfAvatar renders the initials fallback. */
      avatar: string
      competencies: Array<{ title: string; tops: string[]; tips: string[] }>
    }>
  }
  sections: ICollectionPdfSection[]
}

/**
 * Company-wide results of one evaluation term — the print twin of the term
 * evaluations "Results" tab: the KPI strip, the culture strip, the nine-grid
 * distribution and a row per member. Assembled client-side by
 * buildTermResultsData from the same endpoints the tab itself reads.
 */
export interface ITermResultsPdfData {
  companyName: string
  /** Logo as a data URI (or '' when unavailable / blocked by CORS). */
  companyLogo: string
  /** Term display name, e.g. "New era" or "#3". */
  termLabel: string
  /** Pre-formatted term period, e.g. "12-06-2026 – 12-09-2026" (or '' when unknown). */
  termPeriod: string
  /** Pre-formatted export date for the cover footer (the "exported on" word is a label). */
  exportDate: string
  /** Labels of the filters the cohort was narrowed by; empty when unfiltered. */
  filterLabels: string[]
  /** Cohort size the document reports on. */
  memberCount: number
  /** KPI strip: cohort averages, each with how many members carried a score. */
  kpis: {
    goalAchievement?: number
    skill?: number
    culture?: number
    measuredGoalMembers: number
    measuredSkillMembers: number
    measuredCultureMembers: number
    totalGoalMembers: number
    totalSkillMembers: number
    totalCultureMembers: number
  }
  /**
   * Culture strip: the values as they stood during this term (from the trends
   * snapshot, not the live company), each with the cohort's average alignment.
   */
  cultureValues: Array<{
    subtitle: string
    /** Resolved hex fill for the value's ring. */
    color: string
    /** 0–100 cohort average; undefined when the value went unscored. */
    score?: number
  }>
  /**
   * Nine-grid distribution: how many of the cohort landed in each cell.
   * zoneX = culture band, zoneY = skill band, both 0 (low) → 2 (high).
   */
  nineGrid: {
    zones: Array<{ zoneX: number; zoneY: number; count: number }>
    skillMin: string
    skillMax: string
    cultureMin: string
    cultureMax: string
  }
  /** Company evaluation scales, for the per-member score bars. */
  skillScale: Array<{ value: number; label: string }>
  cultureScale: Array<{ value: number; label: string }>
  /** One row per member, in the order the tab lists them. */
  members: Array<{
    name: string
    jobTitle: string
    /** 0–100 average, or undefined when unscored / not assessed. */
    skillScore?: number
    /** Nearest scale step label for skillScore ('' when unscored). */
    skillLabel: string
    cultureScore?: number
    cultureLabel: string
    goalsTotal: number
    goalsAchieved: number
    hasSkillAssessors: boolean
    hasCultureAssessors: boolean
    hasGoalAssessors: boolean
  }>
  colors: IJobProfilePdfData['colors']
}
